import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmtDate } from "../lib/format";

function fmtSpan(raw) {
  const m = String(raw || "").match(/(\d{2})(\d{2})\s*-\s*(\d{2})(\d{2})/);
  if (!m) return raw || "—";
  const t = (h, mm) => `${String((+h + 11) % 12 + 1)}:${mm} ${+h >= 12 ? "PM" : "AM"}`;
  return `${t(m[1], m[2])} – ${t(m[3], m[4])}`;
}

export default function EmployeeVisitsPage() {
  const { uid } = useParams();
  const [emp, setEmp] = useState(null);
  const [data, setData] = useState(null);
  const [year, setYear] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/users/${uid}`).then((r) => setEmp(r.data.user || r.data)).catch(() => setEmp(null));
  }, [uid]);
  useEffect(() => {
    setData(null);
    api.get(`/employees/${uid}/visits`, { params: { year, page } })
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setData({ visits: [], total: 0, page: 1, per_page: 200 }); });
  }, [uid, year, page]);

  const s = data?.summary;
  const pages = data ? Math.max(1, Math.ceil((data.total || 0) / (data.per_page || 200))) : 1;

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-6 py-8" data-testid="employee-visits-page">
      <Link to={`/team/${uid}`} className="inline-flex items-center gap-1 text-sm font-medium text-stone-500 hover:text-stone-800" data-testid="visits-back-link">
        <ArrowLeft size={14} /> Back to employee file
      </Link>
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Visit history — {emp?.name || "…"}</h1>
        <p className="mt-1 text-sm text-stone-500">Every visit from the imported visit log, newest first.</p>
      </div>

      {s && (
        <div className="flex flex-wrap gap-3" data-testid="visits-summary">
          {[
            ["Visits", s.visits],
            ["Hours", s.hours?.toLocaleString()],
            ["Clients", s.clients],
            ["Missed", s.missed],
            ["Span", s.first && s.last ? `${s.first} → ${s.last}` : "—"],
          ].map(([label, v]) => (
            <div key={label} className="rounded-xl border border-stone-200 bg-white px-5 py-3 shadow-sm" data-testid={`visits-stat-${label.toLowerCase()}`}>
              <p className="text-[10px] font-bold uppercase tracking-wide text-stone-400">{label}</p>
              <p className="text-lg font-semibold text-stone-900">{v ?? "—"}</p>
            </div>
          ))}
          {s.years?.length > 0 && (
            <select value={year} onChange={(e) => { setYear(e.target.value); setPage(1); }}
              className="ml-auto h-10 self-center rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="visits-year-filter">
              <option value="">All years</option>
              {s.years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
        </div>
      )}

      <div className="rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="visits-table">
        {!data ? (
          <p className="px-6 py-10 text-center text-sm text-stone-400">Loading visits…</p>
        ) : data.visits.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm italic text-stone-400" data-testid="visits-empty">No visits on record{year ? ` for ${year}` : ""}.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[10px] font-bold uppercase tracking-wide text-stone-400">
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Scheduled</th>
                <th className="px-5 py-3">Worked</th>
                <th className="px-5 py-3">Hours</th>
                <th className="px-5 py-3">Billed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.visits.map((v) => (
                <tr key={v.id} className={v.missed ? "bg-[#C0392B]/5" : ""} data-testid={`visit-row-${v.id}`}>
                  <td className="whitespace-nowrap px-5 py-2 font-medium text-stone-800">{fmtDate(v.visit_date)}</td>
                  <td className="px-5 py-2 text-stone-700">
                    {v.client_id ? (
                      <Link to={`/clients/${v.client_id}`} className="font-medium text-primary hover:underline">{v.client_name}</Link>
                    ) : v.client_name}
                  </td>
                  <td className="whitespace-nowrap px-5 py-2 text-stone-500">{fmtSpan(v.scheduled_raw)}</td>
                  <td className="whitespace-nowrap px-5 py-2 text-stone-500">{v.missed ? <span className="font-semibold text-[#C0392B]">missed</span> : fmtSpan(v.worked_raw)}</td>
                  <td className="px-5 py-2 text-stone-700">{v.worked_minutes ? (v.worked_minutes / 60).toFixed(1) : "—"}</td>
                  <td className="px-5 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${v.billed ? "bg-[#27AE60]/10 text-[#1e8449]" : "bg-stone-100 text-stone-400"}`}
                      data-testid={`visit-billed-${v.id}`}>{v.billed ? "Billed" : "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && pages > 1 && (
          <div className="flex items-center justify-between border-t border-stone-100 px-5 py-3 text-xs text-stone-500">
            <span data-testid="visits-page-info">Page {data.page} of {pages} · {data.total.toLocaleString()} visits</span>
            <span className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 font-semibold disabled:opacity-40" data-testid="visits-prev">
                <CaretLeft size={12} /> Newer
              </button>
              <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 font-semibold disabled:opacity-40" data-testid="visits-next">
                Older <CaretRight size={12} />
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
